import { useSiteDataQuery } from 'codegen/generated/cf'
import type {
  NewsletterPopup,
  NotificationPopup,
  ProfileNudgePopup,
  AwaitingRenewalConfirmationBanner,
  ClassesBanner,
  NotificationBanner,
  OnHoldSubscriptionBanner,
  SparkFreeTrialBanner,
  AllAccessBanner,
  UndoCancelationBanner,
  UpsellBanner,
  VerificationBanner,
  AllAccessYearlyBanner,
  AllAccessYearlyTrialBanner,
  AllAccessYearlyExtendBanner,
} from 'codegen/types'
import { keepPreviousData } from '@tanstack/react-query'

export type Popups = NewsletterPopup | NotificationPopup | ProfileNudgePopup
export type Banners =
  | AwaitingRenewalConfirmationBanner
  | ClassesBanner
  | NotificationBanner
  | OnHoldSubscriptionBanner
  | SparkFreeTrialBanner
  | AllAccessBanner
  | UndoCancelationBanner
  | UpsellBanner
  | VerificationBanner
  | AllAccessYearlyBanner
  | AllAccessYearlyTrialBanner
  | AllAccessYearlyExtendBanner

export const siteDataQueryOptions = {
  placeholderData: keepPreviousData,
  staleTime: Infinity,
}

// eslint-disable-next-line cf-web/codegen-query-returns
export function useSiteData() {
  return useSiteDataQuery(undefined, {
    ...siteDataQueryOptions,
    select(data) {
      return data.siteData
    },
  })
}

export function useSitePopup() {
  const { data } = useSiteData()

  let popup: Popups | null = null

  if (data?.newsletterPopup) {
    popup = data.newsletterPopup
  }

  if (data?.notificationPopup) {
    popup = data.notificationPopup
  }

  if (data?.profileNudgePopup) {
    popup = data.profileNudgePopup
  }

  return popup
}

// oxlint-disable-next-line complexity
export function useSiteBanner() {
  const { data, isFetched } = useSiteData()
  let banner: Banners | null = null

  if (data?.awaitingRenewalConfirmationBanner) {
    banner = data.awaitingRenewalConfirmationBanner
  }

  if (data?.classesBanner) {
    banner = data.classesBanner
  }

  if (data?.notificationBanner) {
    banner = data.notificationBanner
  }

  if (data?.onHoldSubscriptionBanner) {
    banner = data.onHoldSubscriptionBanner
  }

  if (data?.sparkFreeTrialBanner) {
    banner = data.sparkFreeTrialBanner
  }

  if (data?.allAccessBanner) {
    banner = data.allAccessBanner
  }

  if (data?.undoCancelationBanner) {
    banner = data.undoCancelationBanner
  }

  if (data?.upsellBanner) {
    banner = data.upsellBanner
  }

  if (data?.verificationBanner) {
    banner = data.verificationBanner
  }

  if (data?.allAccessYearlyBanner) {
    banner = data.allAccessYearlyBanner
  }

  if (data?.allAccessYearlyTrialBanner) {
    banner = data.allAccessYearlyTrialBanner
  }

  if (data?.allAccessYearlyExtendBanner) {
    banner = data.allAccessYearlyExtendBanner
  }

  return { banner, isFetched }
}

export function useIsStripeBillingEnabled() {
  const { data } = useSiteData()
  return Boolean(data?.billing)
}
